import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

/**
 * Content Tooltip Controller
 * 
 * Provides lazy-loaded Bootstrap tooltips for linked content items.
 * When hovering over a linked content name, fetches content details via AJAX
 * and displays them in a tooltip. Converts to a link once content is found.
 * 
 * Features:
 * - Image preview (if content has image)
 * - Type icon and book badge
 * - Edit button for admin users
 * - Mobile: tap to show, tap again to navigate
 * 
 * Usage:
 * <span data-controller="content-tooltip"
 *       data-content-tooltip-name-value="Kampfreflexe"
 *       data-content-tooltip-types-value="Advantage,Specialability"
 *       data-content-tooltip-language-value="de"
 *       data-content-tooltip-source-type-value="Career"
 *       data-content-tooltip-source-field-value="requirements">
 *   Kampfreflexe 2
 * </span>
 */
export default class extends Controller {
  static values = {
    name: String,           // Base name of the content (without level number)
    types: String,          // Comma-separated list of content types to search
    language: String,       // Language to filter by (de/en)
    sourceType: String,     // Content type that contains this link (for tooltip field config)
    sourceField: String,    // Field name in sourceType that contains this link
    loaded: { type: Boolean, default: false },
    url: String,            // URL to linked content (set after lookup)
    mobileShown: { type: Boolean, default: false }  // Track mobile tooltip state
  }

  static classes = ["loading", "found", "notFound"]

  connect() {
    this.tooltip = null
    this.contentData = null
    this.isTouchDevice = this.checkTouchDevice()
    
    // Add hover styling
    this.element.classList.add("content-link")
    
    // Initialize Bootstrap tooltip with loading state
    this.initTooltip()
    
    // Add mobile touch handling
    if (this.isTouchDevice) {
      this.element.addEventListener("click", this.handleMobileClick.bind(this))
    }
  }

  disconnect() {
    this.destroyTooltip()
  }
  
  checkTouchDevice() {
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
  }

  initTooltip() {
    // Create Bootstrap tooltip with custom HTML
    this.tooltip = new bootstrap.Tooltip(this.element, {
      html: true,
      trigger: this.isTouchDevice ? "manual" : "hover focus",
      placement: "top",
      customClass: "content-tooltip",
      title: () => this.getTooltipContent()
    })

    // Fetch data on first show
    this.element.addEventListener("show.bs.tooltip", () => {
      if (!this.loadedValue) {
        this.fetchContent()
      }
    })
  }

  destroyTooltip() {
    if (this.tooltip) {
      this.tooltip.dispose()
      this.tooltip = null
    }
  }

  getTooltipContent() {
    if (!this.loadedValue) {
      return this.loadingTemplate()
    }
    
    if (this.contentData) {
      return this.contentTemplate(this.contentData)
    }
    
    return this.notFoundTemplate()
  }

  async fetchContent() {
    try {
      const params = new URLSearchParams({
        name: this.nameValue,
        types: this.typesValue,
        language: this.languageValue,
        source_type: this.sourceTypeValue,
        source_field: this.sourceFieldValue
      })

      const response = await fetch(`/api/v1/content_lookup?${params}`, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.found) {
          this.contentData = data.content
          this.urlValue = data.content.url
          this.element.classList.add("content-link-found")
          // Convert to clickable link
          this.convertToLink(data.content.url)
        } else {
          this.contentData = null
          this.element.classList.add("content-link-not-found")
        }
      } else {
        this.contentData = null
        this.element.classList.add("content-link-not-found")
      }
    } catch (error) {
      console.error("Failed to fetch content:", error)
      this.contentData = null
      this.element.classList.add("content-link-not-found")
    }

    this.loadedValue = true
    
    // Update tooltip content
    if (this.tooltip) {
      this.tooltip.setContent({ '.tooltip-inner': this.getTooltipContent() })
    }
  }

  convertToLink(url) {
    // Make the element clickable
    this.element.style.cursor = "pointer"
    this.element.setAttribute("role", "link")
    this.element.setAttribute("tabindex", "0")
    
    // Add click handler (desktop only - mobile handled separately)
    if (!this.isTouchDevice) {
      this.element.addEventListener("click", (e) => {
        e.preventDefault()
        window.location.href = url
      })
    }
    
    // Add keyboard handler for accessibility
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        window.location.href = url
      }
    })
  }

  /**
   * Handle mobile tap: first tap shows tooltip, second tap navigates
   */
  handleMobileClick(e) {
    if (!this.loadedValue) {
      // First tap on unloaded content - show tooltip and fetch
      e.preventDefault()
      this.tooltip?.show()
      this.mobileShownValue = true
      return
    }
    
    if (!this.mobileShownValue) {
      // First tap after content loaded - show tooltip
      e.preventDefault()
      this.tooltip?.show()
      this.mobileShownValue = true
    } else {
      // Second tap - navigate (let click through to convertToLink handler or default)
      if (this.urlValue) {
        e.preventDefault()
        window.location.href = this.urlValue
      }
      this.mobileShownValue = false
    }
  }

  loadingTemplate() {
    return `
      <div class="content-tooltip-loading">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span class="ms-2">Lade...</span>
      </div>
    `
  }

  notFoundTemplate() {
    return `
      <div class="content-tooltip-not-found">
        <i class="fa-solid fa-circle-question"></i>
        <span>Nicht gefunden: ${this.escapeHtml(this.nameValue)}</span>
      </div>
    `
  }

  contentTemplate(content) {
    // Image preview (if available)
    const imageHtml = content.image_url 
      ? `<img src="${content.image_url}" alt="" class="tooltip-image">`
      : (content.type_icon 
          ? `<div class="tooltip-icon"><i class="fa-solid ${content.type_icon}"></i></div>`
          : '')
    
    // Book badge
    const bookBadge = content.book_name 
      ? `<span class="badge badge-book badge-sm">${this.escapeHtml(content.book_name)}</span>`
      : ''
    
    // Fields from server config
    let fieldsHtml = ""
    if (content.data && Object.keys(content.data).length > 0) {
      const fieldItems = Object.entries(content.data)
        .filter(([key, value]) => value)
        .map(([field, value]) => `
          <div class="tooltip-field">
            <span class="tooltip-field-label">${this.escapeHtml(field)}:</span>
            <span class="tooltip-field-value">${this.escapeHtml(this.truncate(String(value), 80))}</span>
          </div>
        `)
        .join("")
      
      if (fieldItems) {
        fieldsHtml = `<div class="tooltip-fields">${fieldItems}</div>`
      }
    }

    const description = content.description 
      ? `<div class="tooltip-description">${this.escapeHtml(this.truncate(content.description, 150))}</div>`
      : ""

    // Edit button for admin users
    const editButton = content.can_edit && content.edit_url
      ? `<a href="${content.edit_url}" class="tooltip-btn tooltip-btn-edit"><i class="fa-solid fa-pen"></i> Bearbeiten</a>`
      : ''

    return `
      <div class="content-tooltip-content">
        <div class="tooltip-header">
          ${imageHtml}
          <div class="tooltip-header-text">
            <strong class="tooltip-name">${this.escapeHtml(content.name)}</strong>
            <div class="tooltip-badges">
              <span class="badge badge-type badge-sm">${this.escapeHtml(content.type_label || content.type)}</span>
              ${bookBadge}
            </div>
          </div>
        </div>
        ${description}
        ${fieldsHtml}
        <div class="tooltip-footer">
          <a href="${content.url}" class="tooltip-btn tooltip-btn-view">Details ansehen →</a>
          ${editButton}
        </div>
      </div>
    `
  }

  truncate(text, length) {
    if (!text) return ""
    const str = String(text)
    if (str.length <= length) return str
    return str.substring(0, length) + "..."
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = String(text)
    return div.innerHTML
  }
}
